/**
 * @voltxampere/math — Complex dense LU with partial pivoting (Sprint 20)
 *
 * The real-valued counterpart in `lu/dense-lu.ts` is the algorithmic
 * template; this file applies the same row-pivoted Gaussian
 * elimination to a complex matrix. Three operational differences:
 *
 *  1. Pivot magnitude uses |z| = √(re² + im²) instead of |x|.
 *     Computed via `Math.hypot` so extreme magnitudes don't
 *     overflow.
 *
 *  2. The elimination factor `factor = work[i,k] / work[k,k]` uses
 *     Smith's complex division (mirror of `complexDiv` in
 *     `complex.ts`) so it stays well-behaved when `re` and `im`
 *     differ by many orders of magnitude — exactly the regime AC
 *     analysis pushes us into when ω ranges over decades.
 *
 *  3. The L factor's diagonal is unit complex 1+0j; U's diagonal
 *     comes from the eliminated work matrix. Singular pivots
 *     (|z| < PIVOT_THRESHOLD) raise `SingularMatrixError`.
 *
 * Per ADR-0038 we ship complex dense LU first; complex sparse LU is
 * deferred to Sprint 21+ (small AC fixtures use ≤ 50 nodes; dense
 * is ≤ 1 ms per solve).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError, SingularMatrixError } from "@voltxampere/shared";
import { complexMatrixClone, createComplexMatrix, createComplexVector } from "./array.js";
/**
 * Numerical zero threshold for pivots. Slightly looser than the real
 * LU's threshold because complex magnitudes accumulate rounding from
 * two scalar multiplies per cell.
 */
const PIVOT_THRESHOLD = 1e-14;
function assertSquare(a, op) {
    if (a.rows !== a.cols) {
        throw new ContractViolationError(`${op}: matrix must be square (got ${String(a.rows)}x${String(a.cols)})`);
    }
}
/**
 * Smith's complex division — re-implemented locally so the LU inner
 * loop pulls raw scalars rather than alloc'ing intermediate
 * `Complex` objects. Returns `[re, im]` of `(ar + ai·j) / (br + bi·j)`.
 */
function divPair(ar, ai, br, bi) {
    if (Math.abs(br) >= Math.abs(bi)) {
        const r = bi / br;
        const denom = br + r * bi;
        return [(ar + ai * r) / denom, (ai - ar * r) / denom];
    }
    const r = br / bi;
    const denom = bi + r * br;
    return [(ar * r + ai) / denom, (ai * r - ar) / denom];
}
/**
 * Read raw `[re, im]` of cell (i,j). Avoids the `Complex` allocation
 * a generic `complexMatrixGet` would do.
 */
function getPair(data, cols, i, j) {
    const base = 2 * (i * cols + j);
    return [data[base] ?? 0, data[base + 1] ?? 0];
}
function setPair(data, cols, i, j, re, im) {
    const base = 2 * (i * cols + j);
    data[base] = re;
    data[base + 1] = im;
}
/**
 * Factor `a` into `P · a = L · U` using partial (row) pivoting on
 * complex magnitudes. The input is not modified — work happens on a
 * clone.
 */
export function complexLuDecompose(a) {
    assertSquare(a, "complexLuDecompose");
    const n = a.rows;
    const work = complexMatrixClone(a);
    const P = new Uint32Array(n);
    for (let i = 0; i < n; i++)
        P[i] = i;
    for (let k = 0; k < n; k++) {
        // Find pivot row: max |work[i,k]| for i ≥ k.
        let pivotRow = k;
        const [pr0, pi0] = getPair(work.data, n, k, k);
        let pivotMag = Math.hypot(pr0, pi0);
        for (let i = k + 1; i < n; i++) {
            const [r, im] = getPair(work.data, n, i, k);
            const mag = Math.hypot(r, im);
            if (mag > pivotMag) {
                pivotMag = mag;
                pivotRow = i;
            }
        }
        if (pivotMag < PIVOT_THRESHOLD) {
            throw new SingularMatrixError(`complexLuDecompose: zero pivot at column ${String(k)} (|pivot|=${String(pivotMag)})`);
        }
        // Swap rows k and pivotRow.
        if (pivotRow !== k) {
            for (let j = 0; j < n; j++) {
                const [tr, ti] = getPair(work.data, n, k, j);
                const [pr, pi] = getPair(work.data, n, pivotRow, j);
                setPair(work.data, n, k, j, pr, pi);
                setPair(work.data, n, pivotRow, j, tr, ti);
            }
            const tmpP = P[k] ?? 0;
            P[k] = P[pivotRow] ?? 0;
            P[pivotRow] = tmpP;
        }
        // Eliminate below the pivot.
        const [pr, pi] = getPair(work.data, n, k, k);
        for (let i = k + 1; i < n; i++) {
            const [ar, ai] = getPair(work.data, n, i, k);
            const [fr, fi] = divPair(ar, ai, pr, pi);
            // Store the multiplier in the L position (below-diagonal of `work`).
            setPair(work.data, n, i, k, fr, fi);
            for (let j = k + 1; j < n; j++) {
                const [wr, wi] = getPair(work.data, n, i, j);
                const [kr, ki] = getPair(work.data, n, k, j);
                // wij -= factor · wkj  (complex multiply)
                const subRe = fr * kr - fi * ki;
                const subIm = fr * ki + fi * kr;
                setPair(work.data, n, i, j, wr - subRe, wi - subIm);
            }
        }
    }
    // Split into L (unit lower) and U (upper).
    const L = createComplexMatrix(n, n);
    const U = createComplexMatrix(n, n);
    for (let i = 0; i < n; i++) {
        setPair(L.data, n, i, i, 1, 0);
        for (let j = 0; j < n; j++) {
            const [r, im] = getPair(work.data, n, i, j);
            if (j < i)
                setPair(L.data, n, i, j, r, im);
            else
                setPair(U.data, n, i, j, r, im);
        }
    }
    return { L, U, P };
}
/**
 * Apply the permutation: `out[i] = b[P[i]]` (interleaved-aware).
 * Returns a fresh complex vector.
 */
function applyPermutation(b, P) {
    const out = createComplexVector(b.length);
    for (let i = 0; i < P.length; i++) {
        const src = P[i] ?? 0;
        const base = 2 * src;
        const re = b.data[base] ?? 0;
        const im = b.data[base + 1] ?? 0;
        out.data[2 * i] = re;
        out.data[2 * i + 1] = im;
    }
    return out;
}
/**
 * Solve `L·U·x = P·b` with a previously-computed factorisation.
 * Returns a fresh complex vector of length `n`.
 */
export function complexLuSolve(lu, b) {
    const n = lu.L.rows;
    if (b.length !== n) {
        throw new ContractViolationError(`complexLuSolve: rhs length ${String(b.length)} ≠ matrix rank ${String(n)}`);
    }
    for (let i = 0; i < b.length; i++) {
        const re = b.data[2 * i] ?? 0;
        const im = b.data[2 * i + 1] ?? 0;
        if (!Number.isFinite(re) || !Number.isFinite(im)) {
            throw new ContractViolationError(`complexLuSolve: rhs[${String(i)}] must be finite (re=${String(re)}, im=${String(im)})`);
        }
    }
    // Forward solve L·y = P·b.
    const pb = applyPermutation(b, lu.P);
    const y = createComplexVector(n);
    for (let i = 0; i < n; i++) {
        let sr = pb.data[2 * i] ?? 0;
        let si = pb.data[2 * i + 1] ?? 0;
        for (let j = 0; j < i; j++) {
            const [lr, li] = getPair(lu.L.data, n, i, j);
            const yr = y.data[2 * j] ?? 0;
            const yi = y.data[2 * j + 1] ?? 0;
            // s -= L[i,j] · y[j]
            sr -= lr * yr - li * yi;
            si -= lr * yi + li * yr;
        }
        // L diagonal is 1+0j → y[i] = s
        y.data[2 * i] = sr;
        y.data[2 * i + 1] = si;
    }
    // Back solve U·x = y.
    const x = createComplexVector(n);
    for (let i = n - 1; i >= 0; i--) {
        let sr = y.data[2 * i] ?? 0;
        let si = y.data[2 * i + 1] ?? 0;
        for (let j = i + 1; j < n; j++) {
            const [ur, ui] = getPair(lu.U.data, n, i, j);
            const xr = x.data[2 * j] ?? 0;
            const xi = x.data[2 * j + 1] ?? 0;
            sr -= ur * xr - ui * xi;
            si -= ur * xi + ui * xr;
        }
        const [dr, di] = getPair(lu.U.data, n, i, i);
        if (Math.hypot(dr, di) < PIVOT_THRESHOLD) {
            throw new SingularMatrixError(`complexLuSolve: zero diagonal in U at row ${String(i)}`);
        }
        const [xr, xi] = divPair(sr, si, dr, di);
        x.data[2 * i] = xr;
        x.data[2 * i + 1] = xi;
    }
    return x;
}
/** Convenience: factor and solve in one call. */
export function complexSolve(a, b) {
    return complexLuSolve(complexLuDecompose(a), b);
}
//# sourceMappingURL=dense-lu.js.map